import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';
import type {
  NotificationChannel,
  NotificationStepConfig,
  NotificationTarget,
  NotificationType,
} from '../../types/pipelineConfig';
import { TimeZoneSelect } from '../../utils/timezones';
import { StepConfigHeader } from './StepConfigHeader';
import { getDefaultNotificationConfig } from '../../utils/pipelineDefaults';

interface NotificationConfigEditorProps {
  config?: NotificationStepConfig;
  onChange: (config: NotificationStepConfig) => void;
  endpointType: string | number;
  disabled?: boolean;
  isSystemDefault?: boolean;
  hideToggle?: boolean;
}

// ---- CONSTANTS ----

const NOTIFICATION_TYPES: { value: NotificationType; label: string; description: string }[] = [
  { value: 'incidentCreated', label: 'Incident Created', description: 'New incident is created from an alert' },
  { value: 'incidentUpdated', label: 'Incident Updated', description: 'Existing incident details are modified' },
  { value: 'incidentResolved', label: 'Incident Resolved', description: 'Incident is marked as resolved' },
  { value: 'incidentClosed', label: 'Incident Closed', description: 'Incident is formally closed' },
  { value: 'incidentEscalated', label: 'Incident Escalated', description: 'Incident priority or assignment is escalated' },
  { value: 'incidentReopened', label: 'Incident Reopened', description: 'Previously resolved incident is reopened' },
  { value: 'incidentAssigned', label: 'Incident Assigned', description: 'Incident is assigned to a team or user' },
  { value: 'slaWarning', label: 'SLA Warning', description: 'SLA target is at risk of being breached' },
  { value: 'slaBreached', label: 'SLA Breached', description: 'SLA target has been exceeded' },
];

const TEMPLATE_OPTIONS: { value: string; label: string; description: string }[] = [
  { value: '', label: 'Default', description: 'Standard notification with key incident fields' },
  { value: 'detailed', label: 'Detailed', description: 'Full incident context including history and related events' },
  { value: 'brief', label: 'Brief', description: 'Compact summary with title, severity, and link only' },
  { value: 'executive', label: 'Executive', description: 'High-level overview for management stakeholders' },
  { value: 'technical', label: 'Technical', description: 'Includes diagnostics, resource IDs, and payload excerpts' },
  { value: 'sla-focused', label: 'SLA-Focused', description: 'Emphasizes SLA targets, deadlines, and breach status' },
];

const WEEKDAYS: { value: number; label: string; short: string }[] = [
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
  { value: 0, label: 'Sunday', short: 'Sun' },
];

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: '📧 Email',
  teams: '💬 Microsoft Teams',
  slack: '💬 Slack',
  webhook: '🔗 Webhook',
  inApp: '🔔 In-App',
};

// ---- HELPERS ----

function createId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

function parseMapRows(value: string): Record<string, string> {
  const out: Record<string, string> = {};
  value.split('\n').map((x) => x.trim()).filter(Boolean).forEach((line) => {
    const [key, ...rest] = line.split('=');
    if (!key) return;
    out[key.trim()] = rest.join('=').trim();
  });
  return out;
}

function stringifyMapRows(value?: Record<string, string>): string {
  if (!value) return '';
  return Object.entries(value).map(([k, v]) => `${k}=${v}`).join('\n');
}

// ---- SUB-COMPONENTS ----

// Label + optional helper text
function FieldLabel({ label, helper, htmlFor }: { label: string; helper?: string; htmlFor?: string }) {
  return (
    <div className="mb-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {helper && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{helper}</p>
      )}
    </div>
  );
}

// Section header within a card
function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="pb-2 border-b border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
      {description && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );
}

// Email chip/tag input
function EmailTagInput({
  emails,
  onChange,
  disabled,
}: {
  emails: string[];
  onChange: (emails: string[]) => void;
  disabled: boolean;
}) {
  const [inputValue, setInputValue] = useState('');

  const addEmail = (raw: string) => {
    const email = raw.trim().toLowerCase();
    if (email && !emails.includes(email)) {
      onChange([...emails, email]);
    }
    setInputValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      onChange(emails.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) addEmail(inputValue);
  };

  const removeEmail = (email: string) => {
    onChange(emails.filter((e) => e !== email));
  };

  return (
    <div>
      {emails.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {emails.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {email}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  className="ml-0.5 hover:text-blue-600 dark:hover:text-blue-100"
                  aria-label={`Remove ${email}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <input
        type="email"
        className="input w-full"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={emails.length === 0 ? 'Enter email address and press Enter' : 'Add another email...'}
      />
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Press Enter or comma to add. Backspace to remove last.
      </p>
    </div>
  );
}

// ---- MAIN COMPONENT ----

export function NotificationConfigEditor({
  config: rawConfig,
  onChange,
  endpointType,
  disabled = false,
  isSystemDefault = false,
  hideToggle,
}: NotificationConfigEditorProps) {
  const defaultConfig = useMemo(() => getDefaultNotificationConfig(endpointType), [endpointType]);
  const config = useMemo<NotificationStepConfig>(() => ({
    ...defaultConfig,
    ...(rawConfig ?? {}),
    targets: rawConfig?.targets ?? defaultConfig.targets ?? [],
  }), [defaultConfig, rawConfig]);
  const isStepEnabled = (config as NotificationStepConfig & { isEnabled?: boolean }).isEnabled ?? true;
  const updateConfig= useCallback(
    (patch: Partial<NotificationStepConfig>) => {
      onChange({ ...config, ...patch });
    },
    [config, onChange],
  );

  const updateTarget = useCallback(
    (id: string, patch: Partial<NotificationTarget>) => {
      updateConfig({
        targets: config.targets.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      });
    },
    [config.targets, updateConfig],
  );

  const addTarget = useCallback(() => {
    updateConfig({
      targets: [
        ...config.targets,
        {
          id: createId('notify'),
          channel: 'email',
          enabled: true,
          emailAddresses: [],
          notificationTypes: ['incidentCreated'],
        },
      ],
    });
  }, [config.targets, updateConfig]);

  const removeTarget = useCallback(
    (id: string) => {
      const nextTargets = config.targets.filter((t) => t.id !== id);
      updateConfig({ targets: nextTargets });
    },
    [config.targets, updateConfig],
  );

  const toggleNotificationType = useCallback(
    (targetId: string, type: NotificationType, checked: boolean) => {
      const target = config.targets.find((t) => t.id === targetId);
      if (!target) return;
      const current = target.notificationTypes ?? [];
      const updated = checked ? [...current, type] : current.filter((t) => t !== type);
      updateTarget(targetId, { notificationTypes: updated });
    },
    [config.targets, updateTarget],
  );

  const toggleWeekday = useCallback(
    (day: number) => {
      const current = config.quietHoursDays ?? [];
      const updated = current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day];
      updateConfig({ quietHoursDays: updated });
    },
    [config.quietHoursDays, updateConfig],
  );

  return (
    <div className="space-y-6">
      <StepConfigHeader
        title="Notification"
        isEnabled={isStepEnabled}
        onToggleEnabled={(enabled) =>
          onChange({ ...config, isEnabled: enabled } as NotificationStepConfig & { isEnabled: boolean })
        }
        isSystemDefault={isSystemDefault}
        disabled={disabled}
        hideToggle={hideToggle}
      />
      <div className={isStepEnabled ? 'space-y-6' : 'space-y-6 opacity-50 pointer-events-none'}>

      {/* ===== GLOBAL SETTINGS CARD ===== */}
      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" aria-label="Global notification settings">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>⚙️ Global Notification Settings</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{config.targets.length} targets</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div className="space-y-6">
          <SectionHeader
            title="Global Notification Settings"
            description="Settings that apply to all notification targets in this pipeline."
          />

        {/* Template Selection */}
        <div>
          <FieldLabel
            label="Notification Template"
            helper="Select the layout and content style for notification messages."
          />
          <select
            className="input w-full max-w-md"
            value={config.templateId ?? ''}
            onChange={(e) => updateConfig({ templateId: e.target.value || undefined })}
            disabled={disabled}
          >
            {TEMPLATE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label} — {t.description}
              </option>
            ))}
          </select>
        </div>

        {/* Digest Notifications */}
        <div className="space-y-3">
          <SectionHeader title="Digest Notifications" />
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.enableDigest}
              onChange={(e) => updateConfig({ enableDigest: e.target.checked })}
              disabled={disabled}
              className="h-4 w-4 rounded mt-0.5"
            />
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Batch notifications into periodic digests
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Instead of sending each notification immediately, group them into a summary
                delivered at regular intervals. Useful for reducing notification fatigue during
                high-volume periods.
              </p>
            </div>
          </label>
          {config.enableDigest && (
            <div className="ml-7">
              <FieldLabel
                label="Digest interval (minutes)"
                helper="How often digest summaries are sent. Minimum 5 minutes, maximum 1440 (24 hours)."
              />
              <input
                type="number"
                min={5}
                max={1440}
                className="input w-40"
                value={config.digestIntervalMinutes}
                onChange={(e) =>
                  updateConfig({
                    digestIntervalMinutes: Math.max(5, Math.min(1440, Number(e.target.value) || 5)),
                  })
                }
                disabled={disabled}
              />
            </div>
          )}
        </div>

        {/* Quiet Hours */}
        <div className="space-y-3">
          <SectionHeader title="Quiet Hours" />
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.quietHoursEnabled}
              onChange={(e) => updateConfig({ quietHoursEnabled: e.target.checked })}
              disabled={disabled}
              className="h-4 w-4 rounded mt-0.5"
            />
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Suppress notifications during quiet hours
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Notifications triggered during quiet hours will be held and delivered
                when quiet hours end. Critical severity notifications can still break through
                depending on target configuration.
              </p>
            </div>
          </label>

          {config.quietHoursEnabled && (
            <div className="ml-7 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.quietHoursUseBusinessHours ?? false}
                  onChange={(e) =>
                    updateConfig({ quietHoursUseBusinessHours: e.target.checked })
                  }
                  disabled={disabled || isSystemDefault}
                  className="h-4 w-4 rounded mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Use customer business hours from SLA configuration
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Automatically derive quiet hours from the customer&apos;s SLA business hours
                    schedule. When enabled, notifications are suppressed outside of the
                    customer&apos;s configured business hours.
                  </p>
                </div>
              </label>
              {isSystemDefault && (
                <p className="ml-7 text-xs text-gray-500 dark:text-gray-400">
                  Business-hours based notifications are customer-specific and must be configured per endpoint.
                </p>
              )}

              {!(config.quietHoursUseBusinessHours ?? false) && (
                <div className="space-y-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel label="Start time" />
                      <input
                        type="time"
                        className="input w-full"
                        value={config.quietHoursStart ?? '22:00'}
                        onChange={(e) => updateConfig({ quietHoursStart: e.target.value })}
                        disabled={disabled}
                      />
                    </div>
                    <div>
                      <FieldLabel label="End time" />
                      <input
                        type="time"
                        className="input w-full"
                        value={config.quietHoursEnd ?? '06:00'}
                        onChange={(e) => updateConfig({ quietHoursEnd: e.target.value })}
                        disabled={disabled}
                      />
                    </div>
                  </div>

                  <div>
                    <FieldLabel
                      label="Timezone"
                      helper="Times are interpreted in this timezone."
                    />
                    <TimeZoneSelect
                      value={config.quietHoursTimezone ?? 'UTC'}
                      onChange={(quietHoursTimezone) => updateConfig({ quietHoursTimezone })}
                      className="w-full max-w-sm"
                      disabled={disabled}
                    />
                  </div>

                  <div>
                    <FieldLabel
                      label="Apply on days"
                      helper="Select which days quiet hours apply. If none selected, quiet hours apply every day."
                    />
                    <div className="flex flex-wrap gap-2 mt-1">
                      {WEEKDAYS.map((day) => {
                        const isSelected = (config.quietHoursDays ?? []).includes(day.value);
                        return (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => toggleWeekday(day.value)}
                            disabled={disabled}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              isSelected
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                            } disabled:opacity-50`}
                            title={day.label}
                          >
                            {day.short}
                          </button>
                        );
                      })}
                    </div>
                    {(config.quietHoursDays ?? []).length === 0 && (
                      <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                        No days selected — quiet hours will apply every day.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Runbook Links */}
        <div className="space-y-3">
          <SectionHeader title="Additional Options" />
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.includeRunbook}
              onChange={(e) => updateConfig({ includeRunbook: e.target.checked })}
              disabled={disabled}
              className="h-4 w-4 rounded mt-0.5"
            />
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Include runbook links in notifications
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                When enabled, notifications will include links to relevant runbooks and
                knowledge base articles associated with the alert rule or incident category.
                Runbooks help on-call engineers quickly find resolution steps without
                searching documentation.
              </p>
            </div>
          </label>
        </div>
        </div>
        </div>
      </details>

      {/* ===== NOTIFICATION TARGETS ===== */}
      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>📋 Notification Targets</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{config.targets.length}</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div className="space-y-4">
          {config.targets.map((target, index) => (
            <details
              key={target.id}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <span className="truncate">
                  #{index + 1} {CHANNEL_LABELS[target.channel] || target.channel || '(unnamed)'}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">
                    {target.notificationTypes?.length ?? 0} triggers
                  </span>
                </span>
              </summary>
              <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                <div className="space-y-5">
                <div className="flex items-center justify-end gap-4">
                  <label className="flex items-center gap-2 cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={target.enabled}
                      onChange={(e) => updateTarget(target.id, { enabled: e.target.checked })}
                      disabled={disabled}
                      className="h-4 w-4 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Enabled
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={() => removeTarget(target.id)}
                    disabled={disabled}
                    className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Remove target"
                    aria-label="Remove notification target"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div>
                  <FieldLabel label={`Target ${index + 1}`} />
                  <select
                    className="input w-full max-w-xs"
                    value={target.channel}
                    onChange={(e) =>
                      updateTarget(target.id, {
                        channel: e.target.value as NotificationChannel,
                      })
                    }
                    disabled={disabled}
                  >
                    {(Object.entries(CHANNEL_LABELS) as [NotificationChannel, string][]).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                {/* Notification Events */}
                <div>
                  <FieldLabel
                    label="Trigger on events"
                    helper="Select which incident lifecycle events should trigger a notification to this target."
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
                    {NOTIFICATION_TYPES.map((nt) => {
                      const isChecked = (target.notificationTypes ?? []).includes(nt.value);
                      return (
                        <label
                          key={nt.value}
                          className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                            isChecked
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) =>
                              toggleNotificationType(target.id, nt.value, e.target.checked)
                            }
                            disabled={disabled}
                            className="h-4 w-4 rounded mt-0.5"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {nt.label}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {nt.description}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {(target.notificationTypes ?? []).length === 0 && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      ⚠ No events selected — this target will not receive any notifications.
                    </p>
                  )}
                </div>

                {/* Channel-Specific Configuration */}
                {target.channel === 'email' && (
                  <div>
                    <FieldLabel
                      label="Recipients"
                      helper="Add email addresses of users who should receive notifications. You can add team distribution lists or individual addresses."
                    />
                    <EmailTagInput
                      emails={target.emailAddresses ?? []}
                      onChange={(emailAddresses) => updateTarget(target.id, { emailAddresses })}
                      disabled={disabled}
                    />
                  </div>
                )}

                {(target.channel === 'teams' || target.channel === 'slack') && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel
                        label="Webhook URL"
                        helper={
                          target.channel === 'teams'
                            ? 'The incoming webhook URL from your Teams channel connector.'
                            : 'The incoming webhook URL from your Slack app configuration.'
                        }
                      />
                      <input
                        className="input w-full"
                        type="url"
                        value={target.webhookUrl ?? ''}
                        onChange={(e) => updateTarget(target.id, { webhookUrl: e.target.value })}
                        disabled={disabled}
                        placeholder={
                          target.channel === 'teams'
                            ? 'https://outlook.office.com/webhook/...'
                            : 'https://hooks.slack.com/services/...'
                        }
                      />
                    </div>
                    <div>
                      <FieldLabel
                        label="Channel Name"
                        helper="Display name for reference. Does not affect delivery."
                      />
                      <input
                        className="input w-full"
                        value={target.channelName ?? ''}
                        onChange={(e) => updateTarget(target.id, { channelName: e.target.value })}
                        disabled={disabled}
                        placeholder="e.g., #ops-incidents"
                      />
                    </div>
                  </div>
                )}

                {target.channel === 'webhook' && (
                  <div>
                    <FieldLabel
                      label="Custom HTTP Headers"
                      helper="One header per line in KEY=VALUE format. Used for authentication or routing."
                    />
                    <textarea
                      className="input w-full min-h-[100px] font-mono text-sm"
                      value={stringifyMapRows(target.customHeaders)}
                      onChange={(e) =>
                        updateTarget(target.id, { customHeaders: parseMapRows(e.target.value) })
                      }
                      disabled={disabled}
                      placeholder={'Authorization=Bearer your-token\nX-Custom-Header=value'}
                    />
                  </div>
                )}

                {target.channel === 'inApp' && (
                  <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      In-app notifications are delivered to the CloudIncidents portal.
                      Users will see notifications in their notification bell based on their
                      role and team assignments.
                    </p>
                  </div>
                )}
                </div>
              </div>
            </details>
          ))}

          <button
            type="button"
            onClick={addTarget}
            disabled={disabled}
            className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors disabled:opacity-50"
          >
            + Add Notification Target
          </button>
          </div>
        </div>
      </details>

      {/* Empty State */}
      {config.targets.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p className="text-sm">No notification targets configured.</p>
          <p className="text-xs mt-1">
            Add a target above to start sending notifications when incidents are processed.
          </p>
        </div>
      )}
      </div>
    </div>
  );
}
